import { studyStreak, totalReviewCount } from "../lib/store";
import { guessTimeZone } from "../lib/time";
import { t } from "../lib/i18n";
import { h } from "../ui/dom";

/**
 * A swipeable, multi-slide card of learning stats at the top of Home — one
 * number and label per slide, dot pagination underneath.
 *
 * Horizontal scroll-snap rather than custom drag/gesture code: native
 * momentum plus `scroll-snap` is smoother on a phone than anything hand-rolled
 * here, and study.ts already owns the one surface (the flashcard) that
 * genuinely needs custom physics, for its four independent directions. This
 * only ever needs left/right.
 *
 * Two slides today (streak, lifetime reviews) — both independent, cheap
 * queries that say nothing about any one collection. A third slide (e.g.
 * "due across everything") could reuse the per-row `dueSummary` calls Home
 * already makes rather than adding a query of its own, but that's follow-up
 * work, not part of this pass.
 */
export function statsCard(): HTMLElement {
  const slides = [
    { emoji: "🔥", label: t("statsStreak") },
    { emoji: "📈", label: t("statsTotalReviewed") },
  ];

  const track = h("div", { class: "stats-track" }, ...slides.map((s) => statSlide(s.emoji, s.label)));
  const dots = h(
    "div",
    { class: "stats-dots", attrs: { role: "tablist" } },
    ...slides.map((_, i) =>
      h("button", {
        class: i === 0 ? "dot active" : "dot",
        attrs: { type: "button", role: "tab", "aria-label": `${i + 1}` },
      }),
    ),
  );
  const card = h("div", { class: "stats-card" }, track, dots);

  // The active dot follows whichever slide is currently snapped into view.
  const dotEls = Array.from(dots.children) as HTMLElement[];
  track.addEventListener(
    "scroll",
    () => {
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      dotEls.forEach((dot, i) => dot.classList.toggle("active", i === index));
    },
    { passive: true },
  );
  dotEls.forEach((dot, i) => {
    dot.addEventListener("click", () => {
      track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
    });
  });

  void loadStats(card);
  return card;
}

function statSlide(emoji: string, label: string): HTMLElement {
  return h("div", { class: "stats-slide" },
    h("span", { class: "stats-emoji", text: emoji, attrs: { "aria-hidden": "true" } }),
    h("span", { class: "stats-value skel skel-value" }),
    h("span", { class: "stats-label muted small", text: label }),
  );
}

/** Best-effort: a broken stats card must never block the collection list below it. */
async function loadStats(card: HTMLElement): Promise<void> {
  const tz = guessTimeZone();
  const slideEls = card.querySelectorAll(".stats-slide");

  try {
    const streak = await studyStreak(tz);
    fillValue(slideEls[0], streak);
  } catch {
    fillValue(slideEls[0], null);
  }

  try {
    const total = await totalReviewCount();
    fillValue(slideEls[1], total);
  } catch {
    fillValue(slideEls[1], null);
  }
}

function fillValue(slideEl: Element | undefined, value: number | null): void {
  const valueEl = slideEl?.querySelector(".stats-value");
  if (!valueEl) return;
  valueEl.textContent = value === null ? "—" : String(value);
  valueEl.className = "stats-value";
}
